import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { ArticleSchema } from '@/components/base/SEOSchema';
import { getArticleBySlug, articles } from '@/mocks/articles';
import { articleImages } from '@/mocks/articleImages';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold font-heading text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您要查看的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const imageUrl = articleImages[article.imageSeq] || articleImages['article-emo-01'];

  return (
    <>
      <ScrollToTop />
      <ArticleSchema article={article} />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10">
          {/* Article Header */}
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 text-xs rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                阅读时长 {article.readTime}
              </span>
            </div>
          </header>

          {/* Article Featured Image */}
          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={imageUrl}
              alt={article.title}
              title={`${article.title} - AI风月`}
              className="w-full h-auto max-h-[500px] object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-lg max-w-none mb-12
              prose-headings:font-heading prose-headings:text-foreground-950
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
              prose-strong:text-foreground-950
              prose-ul:text-foreground-700 prose-li:mb-2
              prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Share & Navigation */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-6 border-t border-background-200/50">
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground-500">分享文章：</span>
              <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200 cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200 cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200 cursor-pointer" aria-label="复制链接">
                <i className="ri-link"></i>
              </button>
            </div>
            <Link
              to={`/category/${article.category}`}
              className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200"
            >
              <i className="ri-arrow-left-line"></i>
              返回{article.categoryName}专区
            </Link>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}